import React, { useMemo, useState } from 'react';
import {
    ComposableMap,
    Geographies,
    Geography,
    ZoomableGroup
} from 'react-simple-maps';
import {
    buildDensityByTopoId,
    getTopoCountryDisplayName,
    resolveCountryToTopoId,
} from '../../utils/countryGeoResolver';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

interface AdminWorldMapProps {
    isDark: boolean;
    geographicDistribution: Map<string, number>;
    activeSessions: any[];
    isLoading?: boolean;
    totalUsers?: number;
}

const AdminWorldMap: React.FC<AdminWorldMapProps> = ({
    isDark,
    geographicDistribution,
    activeSessions,
    isLoading = false,
    totalUsers = 0,
}) => {
    const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
    const [position, setPosition] = useState({ coordinates: [0, 20] as [number, number], zoom: 1 });

    const countryDensity = useMemo(
        () => buildDensityByTopoId(geographicDistribution),
        [geographicDistribution]
    );

    const maxCount = useMemo(
        () => Math.max(1, ...Object.values(countryDensity)),
        [countryDensity]
    );

    const totalLocatedUsers = useMemo(
        () => Object.values(countryDensity).reduce((sum, n) => sum + n, 0),
        [countryDensity]
    );

    const countriesWithUsers = useMemo(
        () => Object.values(countryDensity).filter((n) => n > 0).length,
        [countryDensity]
    );

    const activeCountryCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        activeSessions.forEach((session) => {
            const countryName = session.location?.country;
            if (!countryName) return;
            const topoId = resolveCountryToTopoId(countryName);
            if (!topoId) return;
            counts[topoId] = (counts[topoId] || 0) + 1;
        });
        return counts;
    }, [activeSessions]);

    const activeCountries = useMemo(() => new Set(Object.keys(activeCountryCounts)), [activeCountryCounts]);

    const getFillForCountry = (topoId: string, count: number, isHovered: boolean) => {
        if (isHovered) {
            return isDark ? '#22d3ee' : '#06b6d4';
        }
        if (activeCountries.has(topoId)) {
            return isDark ? '#0e7490' : '#0891b2';
        }
        if (count > 0) {
            const intensity = 0.35 + (count / maxCount) * 0.55;
            return isDark
                ? `rgba(6, 182, 212, ${intensity})`
                : `rgba(6, 182, 212, ${0.25 + (count / maxCount) * 0.45})`;
        }
        return isDark ? '#1e293b' : '#e2e8f0';
    };

    const handleZoom = (direction: 'in' | 'out') => {
        const factor = direction === 'in' ? 1.5 : 0.67;
        const newZoom = Math.min(Math.max(position.zoom * factor, 1), 8);
        setPosition((prev) => ({ ...prev, zoom: newZoom }));
    };

    const handleReset = () => {
        setPosition({ coordinates: [0, 20], zoom: 1 });
    };

    const handleMoveEnd = (pos: { coordinates: [number, number]; zoom: number }) => {
        setPosition(pos);
    };

    const hoveredCount = hoveredCountry ? countryDensity[hoveredCountry] || 0 : 0;
    const hoveredActive = hoveredCountry ? activeCountryCounts[hoveredCountry] || 0 : 0;

    return (
        <div
            className={`relative w-full overflow-hidden rounded-2xl transition-all duration-700 ${isDark
                ? 'bg-[#0f172a] border border-slate-800 shadow-[0_0_50px_-12px_rgba(6,182,212,0.15)]'
                : 'bg-slate-50 border border-slate-200 shadow-xl'
                }`}
            style={{ height: '500px' }}
        >
            <div className="absolute top-6 left-8 z-20 pointer-events-none">
                <div className="flex items-center gap-3 mb-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                    <h3 className={`text-[11px] font-bold uppercase tracking-[0.2em] ${isDark ? 'text-cyan-400' : 'text-cyan-700'}`}>
                        Global Intelligence Matrix
                    </h3>
                </div>
                <p className={`text-2xl font-light tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    User Distribution <span className="text-slate-500 font-extralight ml-2">by country</span>
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    Hover a country to see user count · darker = more users
                </p>
            </div>

            <div className="absolute right-8 top-8 flex flex-col gap-3 z-30">
                <div
                    className={`flex flex-col rounded-xl overflow-hidden border backdrop-blur-xl transition-all ${isDark ? 'bg-slate-900/40 border-slate-800' : 'bg-white/40 border-slate-200'
                        }`}
                >
                    <button
                        type="button"
                        onClick={() => handleZoom('in')}
                        className={`p-3 transition-colors hover:bg-cyan-500/10 ${isDark ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                    </button>
                    <div className={`h-px mx-2 ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`} />
                    <button
                        type="button"
                        onClick={() => handleZoom('out')}
                        className={`p-3 transition-colors hover:bg-cyan-500/10 ${isDark ? 'text-slate-400 hover:text-cyan-400' : 'text-slate-600 hover:text-cyan-600'}`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 12H4" />
                        </svg>
                    </button>
                </div>
                <button
                    type="button"
                    onClick={handleReset}
                    className={`p-3 rounded-xl border backdrop-blur-xl transition-all hover:rotate-45 active:scale-90 ${isDark
                        ? 'bg-slate-900/40 border-slate-800 text-slate-400 hover:text-white'
                        : 'bg-white/40 border-slate-200 text-slate-600 hover:text-slate-900'
                        }`}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                </button>
            </div>

            {isLoading && (
                <div className="absolute inset-0 z-40 flex items-center justify-center bg-slate-900/20 backdrop-blur-[1px]">
                    <div className="h-10 w-10 animate-spin rounded-full border-2 border-cyan-500 border-t-transparent" />
                </div>
            )}

            <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 130, center: [0, 20] }}
                style={{ width: '100%', height: '100%' }}
            >
                <ZoomableGroup
                    zoom={position.zoom}
                    center={position.coordinates}
                    onMoveEnd={handleMoveEnd}
                    minZoom={1}
                    maxZoom={8}
                >
                    <Geographies geography={geoUrl}>
                        {({ geographies }: { geographies: any[] }) =>
                            geographies.map((geo: any) => {
                                const topoId = String(geo.id ?? '');
                                if (!topoId || topoId === '?') return null;

                                const count = countryDensity[topoId] || 0;
                                const isHovered = hoveredCountry === topoId;
                                const fillColor = getFillForCountry(topoId, count, isHovered);

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        onMouseEnter={() => setHoveredCountry(topoId)}
                                        onMouseLeave={() => setHoveredCountry(null)}
                                        style={{
                                            default: {
                                                fill: fillColor,
                                                stroke: isDark ? '#334155' : '#cbd5e1',
                                                strokeWidth: 0.5,
                                                outline: 'none',
                                                transition: 'fill 0.2s ease',
                                            },
                                            hover: {
                                                fill: isDark ? '#22d3ee' : '#06b6d4',
                                                stroke: isDark ? '#67e8f9' : '#0891b2',
                                                strokeWidth: 1,
                                                outline: 'none',
                                                cursor: 'pointer',
                                            },
                                            pressed: {
                                                fill: isDark ? '#0891b2' : '#0e7490',
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ZoomableGroup>
            </ComposableMap>

            <div className="absolute bottom-8 left-8 flex gap-8 z-20 pointer-events-none">
                <div className="flex flex-col">
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Users with location
                    </span>
                    <span className={`text-xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {isLoading ? '…' : totalLocatedUsers}
                        {totalUsers > 0 && !isLoading && (
                            <span className={`text-sm ml-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                                / {totalUsers}
                            </span>
                        )}
                    </span>
                </div>
                <div className="w-px h-8 bg-slate-800/50 mt-1" />
                <div className="flex flex-col">
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Countries
                    </span>
                    <span className={`text-xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {isLoading ? '…' : countriesWithUsers}
                    </span>
                </div>
                <div className="w-px h-8 bg-slate-800/50 mt-1" />
                <div className="flex flex-col">
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Active now
                    </span>
                    <span className={`text-xl font-light ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {activeSessions.length}
                    </span>
                </div>
            </div>

            {hoveredCountry && (
                <div
                    className={`absolute bottom-8 right-8 p-6 rounded-2xl border backdrop-blur-2xl animate-in fade-in zoom-in duration-200 shadow-2xl z-30 min-w-[220px] ${isDark ? 'bg-slate-900/90 border-slate-700 text-white' : 'bg-white/90 border-slate-200 text-slate-900'
                        }`}
                >
                    <div className="flex items-center gap-4 mb-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg shadow-inner ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                            🌍
                        </div>
                        <div>
                            <h4 className="text-lg font-medium leading-tight">
                                {getTopoCountryDisplayName(hoveredCountry)}
                            </h4>
                            <p className="text-[10px] uppercase text-cyan-500 font-bold tracking-widest">
                                {hoveredCount > 0 ? 'Registered users' : 'No users in this country'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-end gap-6">
                        <div>
                            <p className="text-[9px] uppercase text-slate-500 tracking-tighter mb-1">Users</p>
                            <p className="text-3xl font-light tabular-nums">{hoveredCount}</p>
                        </div>
                        {hoveredActive > 0 && (
                            <div>
                                <p className="text-[9px] uppercase text-slate-500 tracking-tighter mb-1">Online now</p>
                                <p className="text-2xl font-light tabular-nums text-emerald-400">{hoveredActive}</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {!isLoading && totalLocatedUsers === 0 && (
                <div
                    className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-4 py-2 rounded-lg text-sm text-center max-w-md ${isDark ? 'bg-slate-900/80 text-slate-400' : 'bg-white/90 text-slate-600'
                        }`}
                >
                    No country data yet. Location is captured when users sign in (device info & sessions).
                </div>
            )}
        </div>
    );
};

export default AdminWorldMap;
